/**
 * LatestUsersContent
 *
 * Body of the admin Latest Users screen: a fixed appbar with the "Users" title,
 * a back button (left) and an eye/order-by toggle action (in the appbar's
 * trailing actions slot), then an infinite-scroll list of `LatestUsersListItem`
 * rows. While the first page is loading a centered `ExpauseLoadingIndicator`
 * replaces the whole `FlexibleHeaderList` (neither list nor appbar are visible
 * during the first fetch); while paginating `FlexibleHeaderList`'s built-in
 * trailing indicator is shown after the rows.
 *
 * Ports Flutter `LatestUsersScreen.build`
 * (`lib/presentation/screens/user/latest_users_screen.dart:100-174`). On web
 * the layout is `FlexibleHeaderList` with a fixed appbar (no flexible/collapsing
 * header) — the same shared layout `CampaignsPage` uses.
 *
 * The eye action mirrors `latest_users_screen.dart:119-143`: tinted
 * `theme.disabled` when sorting by created-timestamp (the default) and
 * `theme.activeRed` when sorting by last-seen-timestamp; pressing it toggles the
 * order-by and reloads from the top (the hook's `toggleOrderBy`).
 *
 * State orchestration lives in `useLatestUsers`, not here.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { LastUsersOrderBy } from '@/domain/enums/user/lastUsersOrderBy';
import { useLatestUsers } from '../hooks/useLatestUsers';
import { LatestUsersListItem } from './LatestUsersListItem';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export interface LatestUsersContentProps {
  /**
   * Tap handler for the appbar's leading/back button. Provided by
   * `LatestUsersPage` (wired to `Navigator.goBack()`); when omitted the appbar
   * renders without a back affordance.
   */
  onLeadingTap?: () => void;
}

export const LatestUsersContent: React.FC<LatestUsersContentProps> = ({ onLeadingTap }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const {
    users,
    hasMore,
    isFetching,
    isFirstLoad,
    orderBy,
    loadMore,
    toggleOrderBy,
  } = useLatestUsers();

  // Eye-icon tint mirrors latest_users_screen.dart:125-129: disabled grey while
  // sorting by created-timestamp (the default), active-red while sorting by
  // last-seen-timestamp.
  const orderToggleColor =
    orderBy === LastUsersOrderBy.CREATED_TIMESTAMP ? theme.disabled : theme.activeRed;

  // First-load gate: while the first page is loading, render a centered loading
  // indicator instead of the FlexibleHeaderList (so neither the list nor the
  // appbar are visible during the first fetch), matching CampaignsPage.
  if (isFirstLoad) {
    return (
      <div
        {...qaAttr({ id: 'latest-users-list', status: 'loading' })}
        style={{
          flex: 1,
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ExpauseLoadingIndicator qa={{ id: 'latest-users-first-load', status: 'loading' }} />
      </div>
    );
  }

  return (
    <FlexibleHeaderList<UserEntity>
      qa={{ id: 'latest-users-list', status: 'ready' }}
      listQa={{ id: 'latest-users-list-virtual' }}
      backgroundColor={theme.background}
      appBarOptions={{
        title: l10n.latestUsers.title,
        // No flexibleSpace → a plain fixed title bar (no collapse/parallax).
        // Standalone screen: render the default back button wired to the page's
        // `Navigator.goBack()` (replaces the retired sheet's drag-indicator
        // dismissal). When `onLeadingTap` is absent, no back affordance shows.
        onLeadingTap,
        actions: (
          <button
            type="button"
            onClick={toggleOrderBy}
            aria-label={l10n.latestUsers.title}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AssetIcon
              path="/assets/images/eye.png"
              width={responsive.icon.large}
              height={responsive.icon.large}
              color={orderToggleColor}
              qa={{ id: 'latest-users-order-toggle', status: orderBy }}
            />
          </button>
        ),
      }}
      items={users}
      getItemKey={(user) => user.firebaseId}
      renderItem={(user) => <LatestUsersListItem user={user} />}
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoadingMore={isFetching}
    />
  );
};
